import type { TFilterRelationKind } from 'src/services/storage/project/scheme/objects/filters/types'

/** How a related scheme is reached from the current one */
export type TRelationKind = TFilterRelationKind

/** `t` of the `table` namespace: only the shape the labels need */
export type TRelationLabelTranslate = (
  key: string,
  options?: Record<string, unknown>
) => string

/** Texts of the kind labels, resolved from the `table` namespace once per render */
export interface IRelationLabelTexts {
  parent: string
  children: string
  field: (fieldTitle: string) => string
  linkedAs: (fieldTitle: string) => string
}

export const relationLabelTexts = (
  t: TRelationLabelTranslate
): IRelationLabelTexts => ({
  parent: t('relationParent'),
  children: t('relationChildren'),
  field: (fieldTitle) => t('relationField', { field: fieldTitle }),
  linkedAs: (fieldTitle) => t('relationLinkedAs', { field: fieldTitle }),
})

/**
 * The muted label next to a related scheme: "parent", "children",
 * "field: Deals" (a relationship field of this scheme pointing at it),
 * "linked as Customer" (objects of that scheme whose field points here).
 */
export const getRelationKindLabel = (
  texts: IRelationLabelTexts,
  kind: TRelationKind,
  fieldTitle?: string
): string => {
  switch (kind) {
    case 'parent':
      return texts.parent
    case 'children':
      return texts.children
    case 'field':
      return texts.field(fieldTitle || '')
    case 'reverse':
      return texts.linkedAs(fieldTitle || '')
    default:
      return ''
  }
}

/** "Company parent": the header as one string, for search and tooltips */
export const getRelationSectionTitle = (
  schemeTitle: string,
  kindLabel?: string
): string => (kindLabel ? `${schemeTitle} ${kindLabel}` : schemeTitle)

/** Whether a (lower-cased, trimmed) search term matches a section header */
export const sectionMatchesSearch = (
  needle: string,
  schemeTitle: string,
  kindLabel?: string
): boolean =>
  Boolean(needle) &&
  getRelationSectionTitle(schemeTitle, kindLabel).toLowerCase().includes(needle)
